/**
 * Khung ghép tài khoản Pancake với nhân sự ở thẻ «Data & chốt». Số cộng cho người chỉ từ lúc cấp trên
 * xác nhận; không ai tự xác nhận cho mình. Máy chủ gác quyền, màn chỉ vẽ nút máy chủ cho phép.
 */
import { useState } from "react";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { ACTION } from "@/lib/auth/actions";
import { usePermission } from "@/lib/authv2/permissions";
import { Button } from "@/ui/button";
import { type SalesPancakeAccount, type SalesPancakeAccounts, usePancakeAccountAction, usePancakeAccounts } from "../api";

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : "Không lưu được tài khoản Pancake";
}

export function PancakeAccountsPanel() {
  const q = usePancakeAccounts();
  const canPropose = usePermission(ACTION.SalesPancakePropose);

  if (q.isError) return <QueryErrorNote error={q.error} fallback="Không tải được danh sách tài khoản Pancake" />;
  if (!q.data || q.data.items.length === 0) return null;
  const pending = q.data.items.filter((i) => i.status !== "confirmed");
  const confirmed = q.data.items.filter((i) => i.status === "confirmed");

  return (
    <section aria-labelledby="tk-pancake" className="space-y-2 rounded-lg border border-border p-3 md:p-4">
      <h3 id="tk-pancake" className="text-base font-semibold">
        Tài khoản Pancake chưa ghép ({pending.length})
      </h3>
      <p className="text-xs text-muted">Số chưa cộng cho ai tới khi cấp trên xác nhận. Không ai tự xác nhận cho mình.</p>
      <ul className="divide-y divide-border">
        {pending.map((a) => (
          <AccountRow key={a.pancakeUid} a={a} view={q.data} canPropose={canPropose} />
        ))}
      </ul>
      {confirmed.length > 0 ? (
        <details className="text-sm">
          <summary className="flex min-h-11 cursor-pointer items-center text-muted">Đã ghép ({confirmed.length})</summary>
          <ul className="divide-y divide-border">
            {confirmed.map((a) => (
              <AccountRow key={a.pancakeUid} a={a} view={q.data} canPropose={canPropose} />
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function AccountRow({ a, view, canPropose }: { a: SalesPancakeAccount; view: SalesPancakeAccounts; canPropose: boolean }) {
  const propose = usePancakeAccountAction("propose");
  const confirm = usePancakeAccountAction("confirm");
  const revoke = usePancakeAccountAction("revoke");
  const [who, setWho] = useState("");
  const [askRevoke, setAskRevoke] = useState(false);
  const error = propose.error ?? confirm.error ?? revoke.error;
  const others = view.people.filter((p) => p.staffId !== view.myStaffId);
  const meInList = view.people.some((p) => p.staffId === view.myStaffId);

  const note =
    a.status === "unlinked"
      ? "Chưa ai đề nghị"
      : a.status === "proposed"
        ? `Đề nghị cho: ${a.staffName || a.staffId}${a.canConfirm ? "" : " · chờ cấp trên xác nhận"}`
        : `Đã ghép: ${a.staffName || a.staffId}`;

  return (
    <li className="space-y-1 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-words text-sm">
          Tài khoản "{a.displayName || a.pancakeUid}" <span className="text-muted">· {note}</span>
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {a.canPropose && canPropose && meInList ? (
            <Button
              size="sm"
              variant="outline"
              disabled={propose.isPending}
              onClick={() => propose.mutate({ pancakeUid: a.pancakeUid })}
            >
              Của tôi
            </Button>
          ) : null}
          {a.canPropose && canPropose && others.length > 0 ? (
            <>
              <select
                aria-label={`Chọn người cho tài khoản ${a.displayName || a.pancakeUid}`}
                value={who}
                onChange={(e) => setWho(e.target.value)}
                className="min-h-11 min-w-0 max-w-[12rem] rounded-md border border-border bg-white px-2 text-sm"
              >
                <option value="">Chọn người</option>
                {others.map((p) => (
                  <option key={p.staffId} value={p.staffId}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={!who || propose.isPending}
                onClick={() => propose.mutate({ pancakeUid: a.pancakeUid, staffId: who })}
              >
                Gắn
              </Button>
            </>
          ) : null}
          {a.canConfirm ? (
            <Button size="sm" disabled={confirm.isPending} onClick={() => confirm.mutate({ pancakeUid: a.pancakeUid })}>
              Xác nhận
            </Button>
          ) : null}
          {a.canRevoke ? (
            askRevoke ? (
              <>
                <span className="text-xs text-muted">Thôi cộng số từ hôm nay?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate({ pancakeUid: a.pancakeUid })}
                >
                  Đồng ý thu hồi
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAskRevoke(false)}>
                  Thôi
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setAskRevoke(true)}>
                Thu hồi
              </Button>
            )
          ) : null}
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(error)}
        </p>
      ) : null}
    </li>
  );
}
